/**
 * UnoEngine — Core game logic for UNO
 *
 * Manages the 108-card deck, dealing, turn flow, special card effects,
 * wild color choosing, draw stacking, and win detection.
 */

import {
  UnoCard, UnoColor, UnoCardType, UnoDeck, UnoGameState, UnoGameConfig,
  UnoPlayerState, UnoDirection, UnoGameStatus, UnoTurnRecord,
  VisibleUnoGameState, VisibleUnoPlayerState, UnoMove,
  UNO_COLORS, INITIAL_HAND_SIZE,
} from './types';

// ============================================================================
// Deck Creation & Shuffling
// ============================================================================

let cardIdCounter = 0;

function nextCardId(): string {
  return `uno-card-${++cardIdCounter}`;
}

/**
 * Create the standard 108-card UNO deck.
 * Per color: one 0, two each of 1-9, two Skip, two Reverse, two Draw Two.
 * Plus 4 Wild and 4 Wild Draw Four.
 */
export function createDeck(): UnoCard[] {
  const cards: UnoCard[] = [];

  for (const color of UNO_COLORS) {
    // One 0 per color
    cards.push({ id: nextCardId(), color, type: 'number', value: 0 });

    // Two each of 1-9
    for (let v = 1; v <= 9; v++) {
      cards.push({ id: nextCardId(), color, type: 'number', value: v });
      cards.push({ id: nextCardId(), color, type: 'number', value: v });
    }

    // Two each of action cards
    for (let i = 0; i < 2; i++) {
      cards.push({ id: nextCardId(), color, type: 'skip', value: null });
      cards.push({ id: nextCardId(), color, type: 'reverse', value: null });
      cards.push({ id: nextCardId(), color, type: 'draw-two', value: null });
    }
  }

  // 4 Wild and 4 Wild Draw Four
  for (let i = 0; i < 4; i++) {
    cards.push({ id: nextCardId(), color: null, type: 'wild', value: null });
    cards.push({ id: nextCardId(), color: null, type: 'wild-draw-four', value: null });
  }

  return cards;
}

/**
 * Fisher-Yates shuffle.
 */
export function shuffleCards(cards: UnoCard[]): UnoCard[] {
  const shuffled = [...cards];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

// ============================================================================
// UnoEngine Class
// ============================================================================

export class UnoEngine {
  private state: UnoGameState | null = null;

  /**
   * Initialize a new game with the given config.
   */
  initializeGame(config: UnoGameConfig): void {
    const cards = shuffleCards(createDeck());

    // Deal 7 cards to each player
    const players: UnoPlayerState[] = [];
    let cardIndex = 0;

    for (const pc of config.players) {
      const hand = cards.slice(cardIndex, cardIndex + INITIAL_HAND_SIZE);
      cardIndex += INITIAL_HAND_SIZE;

      players.push({
        id: pc.id,
        name: pc.name,
        hand,
        isHuman: pc.isHuman,
        aiDifficulty: pc.aiDifficulty,
        hasCalledUno: false,
        score: 0,
      });
    }

    // Find a valid starting card (must be a number card)
    let topCardIndex = cardIndex;
    while (topCardIndex < cards.length && cards[topCardIndex].type !== 'number') {
      topCardIndex++;
    }

    // If no number card found after dealt cards, just use the first remaining
    if (topCardIndex >= cards.length) {
      topCardIndex = cardIndex;
    }

    const topCard = cards[topCardIndex];

    // Build draw pile from remaining cards (excluding the top card)
    const drawPile = [
      ...cards.slice(cardIndex, topCardIndex),
      ...cards.slice(topCardIndex + 1),
    ];

    this.state = {
      players,
      deck: {
        drawPile,
        discardPile: [topCard],
      },
      currentPlayerIndex: 0,
      direction: 'clockwise',
      topCard,
      chosenColor: topCard.color,
      drawStack: 0,
      gameStatus: { type: 'active' },
      turnHistory: [],
      roundNumber: 1,
    };
  }

  /**
   * Get the full game state (for engine/AI use).
   */
  getGameState(): UnoGameState {
    if (!this.state) throw new Error('Game not initialized');
    return this.state;
  }

  /**
   * Get visible state for a specific player (hides opponent hands).
   */
  getVisibleState(playerId: string): VisibleUnoGameState {
    if (!this.state) throw new Error('Game not initialized');

    const players: VisibleUnoPlayerState[] = this.state.players.map(p => ({
      id: p.id,
      name: p.name,
      cardCount: p.hand.length,
      hand: p.id === playerId ? p.hand : null,
      hasCalledUno: p.hasCalledUno,
      score: p.score,
    }));

    return {
      players,
      currentPlayerIndex: this.state.currentPlayerIndex,
      direction: this.state.direction,
      topCard: this.state.topCard,
      chosenColor: this.state.chosenColor,
      drawStack: this.state.drawStack,
      gameStatus: this.state.gameStatus,
      drawPileCount: this.state.deck.drawPile.length,
      roundNumber: this.state.roundNumber,
    };
  }

  /**
   * Get the current player's ID.
   */
  getCurrentPlayerId(): string | null {
    if (!this.state) return null;
    return this.state.players[this.state.currentPlayerIndex]?.id ?? null;
  }

  /**
   * Get valid cards that the current player can play.
   */
  getValidMoves(playerId: string): UnoCard[] {
    if (!this.state) return [];
    const player = this.state.players.find(p => p.id === playerId);
    if (!player) return [];

    // If there's a draw stack and player doesn't have a Draw Two, they can't play
    if (this.state.drawStack > 0) {
      return player.hand.filter(c => c.type === 'draw-two');
    }

    const topCard = this.state.topCard;
    const activeColor = this.state.chosenColor || topCard.color;

    return player.hand.filter(card => this.isCardPlayable(card, topCard, activeColor));
  }

  /**
   * Check if a specific card can be played on the current top card.
   */
  isCardPlayable(card: UnoCard, topCard: UnoCard, activeColor: UnoColor | null): boolean {
    // Wild cards can always be played
    if (card.type === 'wild' || card.type === 'wild-draw-four') {
      return true;
    }

    // Match by color
    if (card.color === activeColor) return true;

    // Match by number
    if (card.type === 'number' && topCard.type === 'number' && card.value === topCard.value) {
      return true;
    }

    // Match by type (skip on skip, reverse on reverse, draw-two on draw-two)
    if (card.type !== 'number' && card.type === topCard.type) {
      return true;
    }

    return false;
  }

  /**
   * Play a card from the player's hand.
   */
  playCard(playerId: string, cardId: string, chosenColor?: UnoColor): { success: boolean; error?: string } {
    if (!this.state) return { success: false, error: 'Game not initialized' };
    if (this.state.gameStatus.type === 'complete') return { success: false, error: 'Game is over' };

    const currentPlayer = this.state.players[this.state.currentPlayerIndex];
    if (currentPlayer.id !== playerId) return { success: false, error: 'Not your turn' };

    const cardIndex = currentPlayer.hand.findIndex(c => c.id === cardId);
    if (cardIndex === -1) return { success: false, error: 'Card not in hand' };

    const card = currentPlayer.hand[cardIndex];

    // Validate the card can be played
    if (this.state.drawStack > 0 && card.type !== 'draw-two') {
      return { success: false, error: 'Must play a Draw Two or draw cards' };
    }

    const activeColor = this.state.chosenColor || this.state.topCard.color;
    if (!this.isCardPlayable(card, this.state.topCard, activeColor)) {
      return { success: false, error: 'Card cannot be played on current top card' };
    }

    // Wild cards require a chosen color
    if ((card.type === 'wild' || card.type === 'wild-draw-four') && !chosenColor) {
      // Set status to choosing-color so UI can prompt
      this.state.gameStatus = { type: 'choosing-color', playerId };
      return { success: true };
    }

    // Remove card from hand
    currentPlayer.hand.splice(cardIndex, 1);

    // Place card on discard pile
    this.state.deck.discardPile.push(card);
    this.state.topCard = card;

    // Set chosen color
    if (card.type === 'wild' || card.type === 'wild-draw-four') {
      this.state.chosenColor = chosenColor!;
    } else {
      this.state.chosenColor = card.color;
    }

    // Reset UNO call status
    if (currentPlayer.hand.length !== 1) {
      currentPlayer.hasCalledUno = false;
    }

    // Record turn
    this.state.turnHistory.push({
      playerId,
      playerName: currentPlayer.name,
      action: 'play',
      card,
      chosenColor: chosenColor || undefined,
      timestamp: Date.now(),
    });

    // Check for win
    if (currentPlayer.hand.length === 0) {
      this.state.gameStatus = { type: 'complete', winnerId: playerId };
      return { success: true };
    }

    // Apply card effects
    this.applyCardEffect(card);

    // Set game status back to active
    this.state.gameStatus = { type: 'active' };

    return { success: true };
  }

  /**
   * Draw a card for the current player.
   * If the drawn card is playable, the player may play it immediately.
   */
  drawCard(playerId: string): { success: boolean; drawnCard?: UnoCard; canPlay?: boolean; error?: string } {
    if (!this.state) return { success: false, error: 'Game not initialized' };
    if (this.state.gameStatus.type === 'complete') return { success: false, error: 'Game is over' };

    const currentPlayer = this.state.players[this.state.currentPlayerIndex];
    if (currentPlayer.id !== playerId) return { success: false, error: 'Not your turn' };

    // If there's a draw stack, draw that many cards
    if (this.state.drawStack > 0) {
      const count = this.state.drawStack;
      for (let i = 0; i < count; i++) {
        const card = this.drawFromPile();
        if (card) currentPlayer.hand.push(card);
      }
      this.state.drawStack = 0;

      this.state.turnHistory.push({
        playerId,
        playerName: currentPlayer.name,
        action: 'forced-draw',
        timestamp: Date.now(),
      });

      currentPlayer.hasCalledUno = false;
      this.advanceTurn();
      return { success: true };
    }

    // Normal draw: draw one card
    const drawnCard = this.drawFromPile();
    if (!drawnCard) return { success: false, error: 'No cards to draw' };

    currentPlayer.hand.push(drawnCard);
    currentPlayer.hasCalledUno = false;

    // Check if drawn card can be played
    const activeColor = this.state.chosenColor || this.state.topCard.color;
    const canPlay = this.isCardPlayable(drawnCard, this.state.topCard, activeColor);

    if (!canPlay) {
      this.state.turnHistory.push({
        playerId,
        playerName: currentPlayer.name,
        action: 'draw',
        timestamp: Date.now(),
      });
      this.advanceTurn();
    }

    return { success: true, drawnCard, canPlay };
  }

  /**
   * Play a card that was just drawn (if valid).
   */
  playDrawnCard(playerId: string, cardId: string, chosenColor?: UnoColor): { success: boolean; error?: string } {
    return this.playCard(playerId, cardId, chosenColor);
  }

  /**
   * Call UNO when a player has one card left.
   */
  callUno(playerId: string): { success: boolean; error?: string } {
    if (!this.state) return { success: false, error: 'Game not initialized' };

    const player = this.state.players.find(p => p.id === playerId);
    if (!player) return { success: false, error: 'Player not found' };

    if (player.hand.length > 2) {
      return { success: false, error: 'Can only call UNO with 1-2 cards' };
    }

    player.hasCalledUno = true;

    this.state.turnHistory.push({
      playerId,
      playerName: player.name,
      action: 'call-uno',
      timestamp: Date.now(),
    });

    return { success: true };
  }

  /**
   * Challenge a player who forgot to call UNO (they draw 2 penalty cards).
   */
  challengeUno(challengerId: string, targetId: string): { success: boolean; penalized: boolean; error?: string } {
    if (!this.state) return { success: false, penalized: false, error: 'Game not initialized' };

    const target = this.state.players.find(p => p.id === targetId);
    if (!target) return { success: false, penalized: false, error: 'Target not found' };

    if (target.hand.length !== 1 || target.hasCalledUno) {
      return { success: false, penalized: false, error: 'Invalid challenge' };
    }

    // Penalize: draw 2 cards
    for (let i = 0; i < 2; i++) {
      const card = this.drawFromPile();
      if (card) target.hand.push(card);
    }
    target.hasCalledUno = false;

    return { success: true, penalized: true };
  }

  /**
   * Pass turn after drawing (when drawn card can't be played or player chooses not to play it).
   */
  passTurn(playerId: string): { success: boolean; error?: string } {
    if (!this.state) return { success: false, error: 'Game not initialized' };

    const currentPlayer = this.state.players[this.state.currentPlayerIndex];
    if (currentPlayer.id !== playerId) return { success: false, error: 'Not your turn' };

    this.state.turnHistory.push({
      playerId,
      playerName: currentPlayer.name,
      action: 'draw',
      timestamp: Date.now(),
    });

    this.advanceTurn();
    return { success: true };
  }

  /**
   * Check if the game is over.
   */
  isGameOver(): boolean {
    return this.state?.gameStatus.type === 'complete';
  }

  /**
   * Get the winner's player ID.
   */
  getWinner(): string | null {
    if (!this.state || this.state.gameStatus.type !== 'complete') return null;
    return this.state.gameStatus.winnerId;
  }

  /**
   * Serialize game state for persistence.
   */
  serialize(): string {
    return JSON.stringify(this.state);
  }

  /**
   * Deserialize and restore game state.
   */
  deserialize(data: string): void {
    this.state = JSON.parse(data);
  }

  // =========================================================================
  // Private Helpers
  // =========================================================================

  private applyCardEffect(card: UnoCard): void {
    if (!this.state) return;

    switch (card.type) {
      case 'skip':
        this.advanceTurn(); // skip next player
        this.advanceTurn();
        break;

      case 'reverse':
        if (this.state.players.length === 2) {
          // With 2 players, reverse acts like skip
          this.advanceTurn();
          this.advanceTurn();
        } else {
          this.state.direction =
            this.state.direction === 'clockwise' ? 'counter-clockwise' : 'clockwise';
          this.advanceTurn();
        }
        break;

      case 'draw-two':
        this.state.drawStack += 2;
        this.advanceTurn();
        break;

      case 'wild':
        this.advanceTurn();
        break;

      case 'wild-draw-four':
        this.state.drawStack += 4;
        this.advanceTurn();
        break;

      default:
        // Number card — just advance
        this.advanceTurn();
        break;
    }
  }

  private advanceTurn(): void {
    if (!this.state) return;

    const count = this.state.players.length;
    if (this.state.direction === 'clockwise') {
      this.state.currentPlayerIndex = (this.state.currentPlayerIndex + 1) % count;
    } else {
      this.state.currentPlayerIndex = (this.state.currentPlayerIndex - 1 + count) % count;
    }
  }

  private drawFromPile(): UnoCard | null {
    if (!this.state) return null;

    // If draw pile is empty, reshuffle discard pile (except top card)
    if (this.state.deck.drawPile.length === 0) {
      if (this.state.deck.discardPile.length <= 1) return null;

      const topCard = this.state.deck.discardPile.pop()!;
      this.state.deck.drawPile = shuffleCards(this.state.deck.discardPile);
      this.state.deck.discardPile = [topCard];
    }

    return this.state.deck.drawPile.pop() || null;
  }
}
